import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Camera, Receipt } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { HubSaveButton } from "@/components/ui/HubSaveButton";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { ApiError } from "@/lib/api";

import {
  createReimbursementDraft,
  deleteMyReceipt,
  deleteMyReimbursement,
  fetchExpenseCategories,
  fetchMyReimbursement,
  formatReimbursementMoney,
  parseReceiptFile,
  reimbursementEditable,
  reimbursementKindLabel,
  reimbursementStatusLabel,
  saveReimbursementDraft,
  submitMyReimbursement,
  uploadReimbursementReceipt,
  type ReceiptParse,
  type Reimbursement,
  type ReimbursementDraftPatch,
  type ReimbursementKind,
} from "./reimbursementApi";
import { ReceiptThumb } from "./ReimbursementReceiptThumb";
import { WellnessAllotmentNotice } from "./WellnessAllotmentNotice";
import { CategorySelect, reimbursementFieldClass, YesNoField } from "./ReimbursementFields";

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return "Something went wrong.";
}

export function StatusBadge({ status }: { status: Reimbursement["status"] }): JSX.Element {
  const tone =
    status === "reimbursed"
      ? "bg-emerald-100 text-emerald-900"
      : status === "approved"
        ? "bg-sky-100 text-sky-900"
        : status === "rejected"
          ? "bg-amber-100 text-amber-950"
          : status === "submitted"
            ? "bg-indigo-100 text-indigo-900"
            : "bg-slate-100 text-slate-700";
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${tone}`}>
      {reimbursementStatusLabel(status)}
    </span>
  );
}

function draftFrom(row: Reimbursement): ReimbursementDraftPatch {
  return {
    startOn: row.startOn,
    endOn: row.endOn,
    incurredOn: row.incurredOn,
    amountRequested: row.amountRequested,
    explanation: row.explanation,
    destination: row.destination,
    purpose: row.purpose,
    departedBefore9am: row.departedBefore9am,
    returnedAfter5pm: row.returnedAfter5pm,
    companyPaidMealsLodging: row.companyPaidMealsLodging,
    advanceAmount: row.advanceAmount,
    miles: row.miles,
  };
}

export function KindPicker(): JSX.Element {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: (kind: ReimbursementKind) => createReimbursementDraft(kind),
    onSuccess: (row) => navigate(`/personal/reimbursements/${row.id}`, { replace: true }),
    onError: (cause) => setError(errorMessage(cause)),
  });
  const choices: Array<{ kind: ReimbursementKind; detail: string }> = [
    { kind: "expense", detail: "Receipts for travel, trade shows, and office costs." },
    { kind: "per_diem", detail: "Travel dates, destination, and any amount advanced." },
    { kind: "mileage", detail: "Miles driven, destination, and purpose." },
    { kind: "wellness", detail: "Receipt, description, amount, and date of service." },
  ];
  return (
    <div className="space-y-4">
      <PageTitleWithHelp hoverHint="Pick the kind of reimbursement. You can photograph a receipt and the hub will read the amount and date before you submit.">
        New reimbursement
      </PageTitleWithHelp>
      {error ? <p className="text-sm text-red-700">{error}</p> : null}
      <div className="grid gap-3">
        {choices.map((choice) => (
          <button
            key={choice.kind}
            type="button"
            disabled={mutation.isPending}
            className="min-h-16 rounded-lg border border-slate-200 bg-white px-4 py-3 text-left shadow-sm hover:border-slate-300 disabled:opacity-60"
            onClick={() => mutation.mutate(choice.kind)}
          >
            <div className="text-base font-medium text-slate-900">{reimbursementKindLabel(choice.kind)}</div>
            <div className="mt-1 text-sm text-slate-600">{choice.detail}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

export function ReimbursementEditor(): JSX.Element {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["personal", "reimbursements", id],
    queryFn: () => fetchMyReimbursement(id),
    enabled: id !== "",
  });
  const categoriesQuery = useQuery({
    queryKey: ["personal", "reimbursement-categories"],
    queryFn: () => fetchExpenseCategories("personal"),
  });
  const [draft, setDraft] = useState<ReimbursementDraftPatch | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [pendingPreview, setPendingPreview] = useState<string | null>(null);
  const [parseState, setParseState] = useState<ReceiptParse | null>(null);
  const [parsing, setParsing] = useState(false);
  const [lineCategory, setLineCategory] = useState("");
  const [lineAmount, setLineAmount] = useState("");
  const [lineMerchant, setLineMerchant] = useState("");
  const [lineDate, setLineDate] = useState("");
  const [lineDescription, setLineDescription] = useState("");

  useEffect(() => {
    if (query.data) setDraft(draftFrom(query.data));
  }, [query.data]);

  useEffect(() => {
    return () => {
      if (pendingPreview) URL.revokeObjectURL(pendingPreview);
    };
  }, [pendingPreview]);

  const saved = query.data ? draftFrom(query.data) : null;
  const dirty = draft != null && saved != null && JSON.stringify(draft) !== JSON.stringify(saved);
  const editable = query.data != null && reimbursementEditable(query.data.status);

  function patchDraft(partial: ReimbursementDraftPatch): void {
    setDraft((current) => ({ ...(current ?? {}), ...partial }));
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (draft == null) throw new Error("Nothing to save.");
      return saveReimbursementDraft(id, draft);
    },
    onSuccess: async (row) => {
      setMessage("Draft saved.");
      await queryClient.invalidateQueries({ queryKey: ["personal", "reimbursements"] });
      setDraft(draftFrom(row));
    },
    onError: (cause) => setMessage(errorMessage(cause)),
  });

  const submitMutation = useMutation({
    mutationFn: async () => {
      if (draft != null) await saveReimbursementDraft(id, draft);
      return submitMyReimbursement(id);
    },
    onSuccess: async () => {
      setMessage("Submitted to Accounting.");
      await queryClient.invalidateQueries({ queryKey: ["personal", "reimbursements"] });
    },
    onError: (cause) => setMessage(errorMessage(cause)),
  });

  async function onPickFile(file: File | null): Promise<void> {
    setMessage(null);
    if (pendingPreview) URL.revokeObjectURL(pendingPreview);
    setPendingFile(file);
    setParseState(null);
    if (file == null) {
      setPendingPreview(null);
      return;
    }
    setPendingPreview(file.type.startsWith("image/") ? URL.createObjectURL(file) : null);
    setParsing(true);
    try {
      const parsed = await parseReceiptFile(file);
      setParseState(parsed);
      if (parsed.suggestedCategoryKey) setLineCategory(parsed.suggestedCategoryKey);
      if (parsed.amount != null) setLineAmount(String(parsed.amount));
      if (parsed.merchant) setLineMerchant(parsed.merchant);
      if (parsed.incurredOn) setLineDate(parsed.incurredOn);
      if (parsed.description) setLineDescription(parsed.description);
      if (query.data?.kind === "wellness") {
        patchDraft({
          amountRequested: parsed.amount ?? draft?.amountRequested ?? null,
          incurredOn: parsed.incurredOn ?? draft?.incurredOn ?? null,
          explanation: parsed.description ?? draft?.explanation ?? null,
        });
      }
    } catch (cause) {
      setMessage(`${errorMessage(cause)} Enter the details yourself.`);
    } finally {
      setParsing(false);
    }
  }

  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (pendingFile == null) throw new Error("Choose a receipt first.");
      return uploadReimbursementReceipt(id, pendingFile, {
        categoryKey: lineCategory,
        amount: lineAmount,
        merchant: lineMerchant,
        incurredOn: lineDate,
        description: lineDescription,
        aiParse: parseState,
      });
    },
    onSuccess: async (row) => {
      setPendingFile(null);
      if (pendingPreview) URL.revokeObjectURL(pendingPreview);
      setPendingPreview(null);
      setParseState(null);
      setLineCategory("");
      setLineAmount("");
      setLineMerchant("");
      setLineDate("");
      setLineDescription("");
      setMessage("Receipt added.");
      queryClient.setQueryData(["personal", "reimbursements", id], row);
      await queryClient.invalidateQueries({ queryKey: ["personal", "reimbursements"] });
    },
    onError: (cause) => setMessage(errorMessage(cause)),
  });

  if (query.isLoading || draft == null || query.data == null) {
    return <p className="text-sm text-slate-600">Loading request...</p>;
  }
  if (query.error) return <p className="text-sm text-red-700">{errorMessage(query.error)}</p>;
  const row = query.data;
  const showDates = row.kind === "expense" || row.kind === "per_diem" || row.kind === "mileage";

  return (
    <div className="space-y-4 pb-28">
      <PageTitleWithHelp
        breadcrumbLabel={reimbursementKindLabel(row.kind)}
        hoverHint="Photograph a receipt on your phone. The first pass fills merchant, date, amount, and category. Check them before you submit."
      >
        {reimbursementKindLabel(row.kind)}
      </PageTitleWithHelp>
      <div className="flex items-center gap-2">
        <StatusBadge status={row.status} />
        {row.amountRequested != null ? (
          <span className="text-sm tabular-nums text-slate-700">{formatReimbursementMoney(row.amountRequested)}</span>
        ) : null}
      </div>
      {row.accountingNote ? (
        <Card className="border-amber-200 bg-amber-50 p-4 text-sm text-amber-950">
          Accounting: {row.accountingNote}
        </Card>
      ) : null}
      {row.kind === "wellness" && row.wellnessAllotment ? (
        <WellnessAllotmentNotice
          allotment={row.wellnessAllotment}
          thisAmount={draft.amountRequested ?? row.amountRequested ?? 0}
        />
      ) : null}
      {message ? <p className="text-sm text-slate-700">{message}</p> : null}

      {showDates ? (
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block space-y-1">
            <span className="text-sm font-medium text-slate-700">Start date</span>
            <input
              className={reimbursementFieldClass}
              type="date"
              disabled={!editable}
              value={draft.startOn ?? ""}
              onChange={(event) => patchDraft({ startOn: event.target.value || null })}
            />
          </label>
          <label className="block space-y-1">
            <span className="text-sm font-medium text-slate-700">End date</span>
            <input
              className={reimbursementFieldClass}
              type="date"
              disabled={!editable}
              value={draft.endOn ?? ""}
              onChange={(event) => patchDraft({ endOn: event.target.value || null })}
            />
          </label>
        </div>
      ) : null}

      {row.kind === "expense" ? (
        <label className="block space-y-1">
          <span className="text-sm font-medium text-slate-700">Explanation</span>
          <textarea
            className={`${reimbursementFieldClass} min-h-24 py-2`}
            disabled={!editable}
            value={draft.explanation ?? ""}
            onChange={(event) => patchDraft({ explanation: event.target.value })}
          />
        </label>
      ) : null}

      {row.kind === "per_diem" || row.kind === "mileage" ? (
        <>
          <label className="block space-y-1">
            <span className="text-sm font-medium text-slate-700">Destination</span>
            <input
              className={reimbursementFieldClass}
              disabled={!editable}
              value={draft.destination ?? ""}
              onChange={(event) => patchDraft({ destination: event.target.value })}
            />
          </label>
          <label className="block space-y-1">
            <span className="text-sm font-medium text-slate-700">{row.kind === "mileage" ? "Purpose" : "Purpose of travel"}</span>
            <input
              className={reimbursementFieldClass}
              disabled={!editable}
              value={draft.purpose ?? ""}
              onChange={(event) => patchDraft({ purpose: event.target.value })}
            />
          </label>
        </>
      ) : null}

      {row.kind === "per_diem" ? (
        <>
          <YesNoField
            label="Did travel outside the metropolitan area start before 9 am?"
            value={draft.departedBefore9am ?? null}
            disabled={!editable}
            onChange={(next) => patchDraft({ departedBefore9am: next })}
          />
          <YesNoField
            label="Did travel back end after 5 pm?"
            value={draft.returnedAfter5pm ?? null}
            disabled={!editable}
            onChange={(next) => patchDraft({ returnedAfter5pm: next })}
          />
          <label className="block space-y-1">
            <span className="text-sm font-medium text-slate-700">Meals and lodging the company already paid</span>
            <textarea
              className={`${reimbursementFieldClass} min-h-24 py-2`}
              disabled={!editable}
              value={draft.companyPaidMealsLodging ?? ""}
              onChange={(event) => patchDraft({ companyPaidMealsLodging: event.target.value })}
            />
          </label>
          <label className="block space-y-1">
            <span className="text-sm font-medium text-slate-700">Amount advanced before the trip</span>
            <input
              className={reimbursementFieldClass}
              inputMode="decimal"
              disabled={!editable}
              value={draft.advanceAmount ?? ""}
              onChange={(event) => {
                const raw = event.target.value.trim();
                patchDraft({ advanceAmount: raw === "" ? null : Number.parseFloat(raw) });
              }}
            />
          </label>
        </>
      ) : null}

      {row.kind === "mileage" ? (
        <label className="block space-y-1">
          <span className="text-sm font-medium text-slate-700">Miles driven</span>
          <input
            className={reimbursementFieldClass}
            inputMode="decimal"
            disabled={!editable}
            value={draft.miles ?? ""}
            onChange={(event) => {
              const raw = event.target.value.trim();
              patchDraft({ miles: raw === "" ? null : Number.parseFloat(raw) });
            }}
          />
        </label>
      ) : null}

      {row.kind === "wellness" ? (
        <>
          <label className="block space-y-1">
            <span className="text-sm font-medium text-slate-700">Description of good or service</span>
            <textarea
              className={`${reimbursementFieldClass} min-h-24 py-2`}
              disabled={!editable}
              value={draft.explanation ?? ""}
              onChange={(event) => patchDraft({ explanation: event.target.value })}
            />
          </label>
          <label className="block space-y-1">
            <span className="text-sm font-medium text-slate-700">Amount requested</span>
            <input
              className={reimbursementFieldClass}
              inputMode="decimal"
              disabled={!editable}
              value={draft.amountRequested ?? ""}
              onChange={(event) => {
                const raw = event.target.value.trim();
                patchDraft({ amountRequested: raw === "" ? null : Number.parseFloat(raw) });
              }}
            />
          </label>
          <label className="block space-y-1">
            <span className="text-sm font-medium text-slate-700">Date of service or purchase</span>
            <input
              className={reimbursementFieldClass}
              type="date"
              disabled={!editable}
              value={draft.incurredOn ?? ""}
              onChange={(event) => patchDraft({ incurredOn: event.target.value || null })}
            />
          </label>
        </>
      ) : null}

      <div className="space-y-3">
        <h2 className="text-sm font-semibold text-slate-900">Receipts</h2>
        {row.lines.map((line) => {
          const receipt = row.receipts.find((item) => item.lineId === line.id);
          return (
            <Card key={line.id} className="space-y-2 p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-medium text-slate-900">{line.categoryLabel ?? "Receipt"}</div>
                  <div className="text-sm text-slate-600">
                    {[line.merchant, line.incurredOn].filter(Boolean).join(" - ")}
                  </div>
                </div>
                <div className="text-sm font-medium tabular-nums">{formatReimbursementMoney(line.amount)}</div>
              </div>
              {receipt ? <ReceiptThumb scope="personal" reimbursementId={row.id} receipt={receipt} /> : null}
              {editable && receipt ? (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    void deleteMyReceipt(row.id, receipt.id).then((next) => {
                      queryClient.setQueryData(["personal", "reimbursements", id], next);
                    }).catch((cause: unknown) => setMessage(errorMessage(cause)));
                  }}
                >
                  Remove
                </Button>
              ) : null}
            </Card>
          );
        })}
        {row.receipts.filter((receipt) => receipt.lineId == null).map((receipt) => (
          <Card key={receipt.id} className="space-y-2 p-4">
            <ReceiptThumb scope="personal" reimbursementId={row.id} receipt={receipt} />
            {editable ? (
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  void deleteMyReceipt(row.id, receipt.id).then((next) => {
                    queryClient.setQueryData(["personal", "reimbursements", id], next);
                  }).catch((cause: unknown) => setMessage(errorMessage(cause)));
                }}
              >
                Remove receipt
              </Button>
            ) : null}
          </Card>
        ))}
      </div>

      {editable ? (
        <Card className="space-y-3 p-4">
          <div className="flex items-center gap-2 text-sm font-medium text-slate-800">
            <Camera className="h-4 w-4" aria-hidden />
            Add a receipt
          </div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <label className="flex min-h-12 cursor-pointer items-center justify-center rounded-md bg-brand-600 px-3 text-base font-medium text-white">
              Take photo
              <input
                className="sr-only"
                type="file"
                accept="image/*"
                capture="environment"
                onChange={(event) => {
                  void onPickFile(event.target.files?.[0] ?? null);
                  event.target.value = "";
                }}
              />
            </label>
            <label className="flex min-h-12 cursor-pointer items-center justify-center rounded-md border border-slate-300 bg-white px-3 text-base font-medium text-slate-800">
              Choose file
              <input
                className="sr-only"
                type="file"
                accept="image/*,application/pdf"
                onChange={(event) => {
                  void onPickFile(event.target.files?.[0] ?? null);
                  event.target.value = "";
                }}
              />
            </label>
          </div>
          {parsing ? <p className="text-sm text-slate-600">Reading the receipt...</p> : null}
          {pendingPreview ? <img src={pendingPreview} alt="Receipt preview" className="max-h-64 rounded-md border border-slate-200" /> : null}
          {pendingFile && !pendingPreview ? <p className="text-sm text-slate-600">{pendingFile.name}</p> : null}
          {pendingFile && row.kind === "expense" ? (
            <>
              <CategorySelect
                categories={categoriesQuery.data ?? []}
                value={lineCategory}
                disabled={false}
                onChange={setLineCategory}
              />
              <label className="block space-y-1">
                <span className="text-sm font-medium text-slate-700">Amount</span>
                <input className={reimbursementFieldClass} inputMode="decimal" value={lineAmount} onChange={(event) => setLineAmount(event.target.value)} />
              </label>
              <label className="block space-y-1">
                <span className="text-sm font-medium text-slate-700">Merchant</span>
                <input className={reimbursementFieldClass} value={lineMerchant} onChange={(event) => setLineMerchant(event.target.value)} />
              </label>
              <label className="block space-y-1">
                <span className="text-sm font-medium text-slate-700">Receipt date</span>
                <input className={reimbursementFieldClass} type="date" value={lineDate} onChange={(event) => setLineDate(event.target.value)} />
              </label>
            </>
          ) : null}
          {pendingFile ? (
            <Button type="button" className="min-h-12 w-full" isLoading={uploadMutation.isPending} onClick={() => uploadMutation.mutate()}>
              <Receipt className="h-4 w-4" aria-hidden />
              Add this receipt
            </Button>
          ) : null}
        </Card>
      ) : null}

      <div className="fixed inset-x-0 bottom-0 z-20 border-t border-slate-200 bg-white/95 p-3 backdrop-blur">
        <div className="mx-auto flex max-w-3xl flex-wrap gap-2">
          {editable ? (
            <>
              <HubSaveButton
                className="min-h-12 flex-1"
                dirty={dirty}
                isLoading={saveMutation.isPending}
                onClick={() => saveMutation.mutate()}
              >
                Save draft
              </HubSaveButton>
              <Button type="button" className="min-h-12 flex-1" isLoading={submitMutation.isPending} onClick={() => submitMutation.mutate()}>
                Submit
              </Button>
            </>
          ) : (
            <Button type="button" variant="secondary" className="min-h-12" onClick={() => navigate("/personal/reimbursements")}>
              Back to my requests
            </Button>
          )}
          {editable ? (
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                void deleteMyReimbursement(id).then(() => navigate("/personal/reimbursements")).catch((cause: unknown) => {
                  setMessage(errorMessage(cause));
                });
              }}
            >
              Delete
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
